/**
 * customerMeasurementHelp.js — gentle, jargon-free measurement guidance shown
 * to customers on the AI review screen.
 *
 * Used by EditableReview.js. Intentionally simpler than the tailor-side help
 * dictionary: short, reassuring sentences. Skips production warnings, mistakes
 * and consequences (those live in tailorMeasurementHelp.js for tailors).
 */

// One-line "How to measure" tip per field.
export const CUSTOMER_HOW_TO = {
  bust:             'Around the fullest part of your bust, tape kept level.',
  upper_bust:       'Just under the armpits, above the bust — tape level.',
  under_bust:       'Snug around the ribcage, just below the bust.',
  chest:            'Around the fullest part of your chest, under the armpits.',
  waist:            'Around the narrowest part of your natural waist.',
  hip:              'Around the fullest part of your hips, tape level.',
  shoulder:         'Across the back, from one shoulder bone tip to the other.',
  neck:             'Around the base of your neck where a collar would sit.',
  sleeve_length:    'From the shoulder bone tip down to where you want the sleeve to end.',
  sleeve_round:     'Around the bicep at its fullest, arm relaxed.',
  bicep:            'Around the upper arm at the thickest part, relaxed.',
  cuff:             'Around your wrist, with a finger of comfort space.',
  armhole:          'Around the armhole opening, following the body curve.',
  blouse_length:    'From the shoulder seam down to where you want the hem.',
  back_length:      'From the back-neck base, straight down to the hem.',
  side_length:      'From the bottom of the armhole down to the hem.',
  princess_length:  'From shoulder seam, over the bust apex, down to the hem.',
  apex_distance:    'Distance between the two bust apex points.',
  front_neck_depth: 'How deep the front neckline drops from the collarbone.',
  back_neck_depth:  'How deep the back neckline drops below the nape.',
  front_neck:       'How deep the front neckline drops from the collarbone.',
  neck_width:       'Width across the neckline opening, shoulder to shoulder seam.',
  kurta_length:     'From shoulder seam down to your preferred kurta hem.',
  kurti_length:     'From shoulder seam down to your preferred kurti hem.',
  shirt_length:     'From shoulder seam down to your preferred shirt hem.',
  pajama_length:    'From your natural waist down to the ankle.',
  inseam:           'Inner leg seam from crotch to the ankle.',
  outseam:          'Outer leg seam from waist to the ankle.',
  thigh:            'Around the fullest part of the thigh.',
  knee:             'Around the kneecap.',
  bottom_hem:       'Width of the trouser hem opening.',
  cup_shaping:      'How sculpted the bust cups should be — soft, structured or padded.',
  margin_allowance: 'Extra fabric kept inside seams for future alterations.',
  darts:            'Stitched folds that shape the garment to your body.',
};

// Customer-side per-field confidence model.
// Strategy: base = overall fit_confidence from the fusion result. We adjust
// slightly per field type to reflect how reliably the AI infers each one.
// Photos give a small global boost.
const FIELD_DELTA = {
  // Strong primaries — directly anchored to height/weight/size + photo
  bust: +3, chest: +3, waist: +1, hip: +1, shoulder: 0,
  blouse_length: -2, kurta_length: -2, kurti_length: -2, shirt_length: -2,
  sleeve_length: -3, neck: -2, neck_width: -3,
  // Niche — derived from ratios; lower confidence
  upper_bust: -5, under_bust: -4,
  armhole: -7, sleeve_round: -7, bicep: -7, cuff: -4,
  front_neck: -6, front_neck_depth: -6, back_neck_depth: -8,
  apex_distance: -10, princess_length: -10,
  side_length: -6, back_length: -5,
  cup_shaping: -12, margin_allowance: -12, darts: -15,
  inseam: 0, outseam: -2, thigh: -4, knee: -5, bottom_hem: -4,
};

export const fieldConfidence = ({ fieldKey, baseConfidence = 75, hasPhoto = false }) => {
  const delta = FIELD_DELTA[fieldKey] ?? -4;
  const photoBoost = hasPhoto ? 4 : 0;
  return Math.max(40, Math.min(99, Math.round(baseConfidence + delta + photoBoost)));
};

// Essential set — visible by default in the customer review.
// Everything else with a non-null humanize() label moves under the
// "Advanced Tailoring Options" accordion.
export const ESSENTIAL_FIELDS = new Set([
  'bust', 'chest', 'waist', 'shoulder',
  'blouse_length', 'kurti_length', 'kurta_length', 'shirt_length', 'length',
  'sleeve_length',
]);
